<template>
  <div class="admin-about">
    <!-- Header -->
    <div class="admin-header">
      <h1 class="admin-title">
        <i class="fas fa-info-circle"></i>
        Hakkımızda Yönetimi
      </h1>
      <button @click="addNewCard" class="btn btn-add">
        <i class="fas fa-plus"></i>
        Yeni Kart Ekle
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Hakkımızda bilgileri yükleniyor...</p>
    </div>

    <!-- Cards List -->
    <div v-if="!loading" class="cards-container">
      
      <!-- No Cards Message -->
      <div v-if="cards.length === 0" class="no-cards">
        <i class="fas fa-info-circle"></i>
        <p>Henüz kart eklenmemiş. İlk kartınızı eklemek için "Yeni Kart Ekle" butonuna tıklayın.</p>
      </div>

      <!-- Cards List -->
      <div v-for="(card, index) in cards" :key="card.id || index" class="card-item">
        <div class="card-header">
          <h3>
            {{ card.title_tr || 'Yeni Kart' }} 
            <span class="card-type-badge" :class="card.card_type">{{ getCardTypeLabel(card.card_type) }}</span>
          </h3>
          <div class="card-actions">
            <button @click="toggleCard(index)" class="btn btn-toggle">
              <i :class="card.expanded ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
            </button>
            <button @click="deleteCard(card, index)" class="btn btn-delete" :disabled="submitting">
              <i class="fas fa-trash"></i>
            </button>
          </div>
        </div>

        <!-- Card Form (Expanded) -->
        <div v-if="card.expanded" class="card-form">
          <div class="form-section">
            <!-- Kart Tipi ve Durum -->
            <div class="form-row">
              <div class="form-group">
                <label>Kart Tipi</label>
                <select v-model="card.card_type">
                  <option value="mission">Misyon</option>
                  <option value="vision">Vizyon</option>
                  <option value="values">Değerler</option>
                  <option value="about">Hakkımızda</option>
                  <option value="team">Ekip</option>
                  <option value="history">Tarihçe</option>
                  <option value="other">Diğer</option>
                </select>
              </div>
              <div class="form-group">
                <label>Sıra No</label>
                <input 
                  v-model.number="card.sort_order" 
                  type="number" 
                  min="1"
                  placeholder="1"
                />
              </div>
            </div>

            <!-- Aktif/Pasif -->
            <div class="status-toggle">
              <label>
                <input 
                  v-model="card.is_active" 
                  type="checkbox" 
                  :true-value="1" 
                  :false-value="0"
                />
                Aktif
              </label>
            </div>

            <!-- Başlıklar -->
            <div class="form-row">
              <div class="form-group">
                <label>Başlık (Türkçe) *</label>
                <input 
                  v-model="card.title_tr" 
                  type="text" 
                  required 
                  placeholder="Başlık..."
                />
              </div>
              <div class="form-group">
                <label>Başlık (İngilizce)</label>
                <input 
                  v-model="card.title_en" 
                  type="text" 
                  placeholder="Title..."
                />
              </div>
            </div>

            <!-- Açıklamalar -->
            <div class="form-row">
              <div class="form-group">
                <label>Açıklama (Türkçe)</label>
                <textarea 
                  v-model="card.description_tr" 
                  rows="4"
                  placeholder="Açıklama..."
                ></textarea>
              </div>
              <div class="form-group">
                <label>Açıklama (İngilizce)</label>
                <textarea 
                  v-model="card.description_en" 
                  rows="4"
                  placeholder="Description..."
                ></textarea>
              </div>
            </div>

            <!-- Görseller -->
            <div class="form-group">
              <label>Arka Plan Görseli</label>
              <input 
                v-model="card.background_image" 
                type="url" 
                placeholder="https://example.com/background.jpg"
              />
              <small class="form-help">Kartın arka plan görseli</small>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label>İkon URL</label>
                <input 
                  v-model="card.icon_url" 
                  type="url" 
                  placeholder="https://example.com/icon.svg"
                />
                <small class="form-help">Kart ikonu URL'si</small>
              </div>
              <div class="form-group">
                <label>İkon SVG</label>
                <textarea 
                  v-model="card.icon_svg" 
                  rows="3"
                  placeholder="<svg>...</svg>"
                ></textarea>
                <small class="form-help">SVG kodu olarak ikon</small>
              </div>
            </div>

            <!-- Arka Plan Önizlemesi -->
            <div v-if="card.background_image" class="image-preview">
              <img 
                :src="card.background_image" 
                :alt="card.title_tr"
                @error="handleImageError"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Form Actions -->
    <div v-if="!loading && cards.length > 0" class="form-actions">
      <button @click="saveAllCards" :disabled="submitting" class="btn btn-submit">
        <i v-if="submitting" class="fas fa-spinner fa-spin"></i>
        {{ submitting ? 'Kaydediliyor...' : 'Tümünü Kaydet' }}
      </button>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { adminApi } from '@/services/adminApi'

export default {
  name: 'AdminAbout',
  setup() {
    // Reactive Data
    const loading = ref(false)
    const submitting = ref(false)
    const cards = ref([])

    // Methods
    const loadCards = async () => {
      try {
        loading.value = true
        const response = await adminApi.getAbout()
        console.log('About API response:', response)
        
        if (response && response.success && response.data) {
          // Her karta expanded property ekle
          cards.value = response.data.map(card => ({
            ...card,
            expanded: false,
            is_active: parseInt(card.is_active),
            sort_order: parseInt(card.sort_order || 999)
          }))
        }
      } catch (error) {
        console.error('Hakkımızda bilgileri yüklenemedi:', error)
        alert('Hakkımızda bilgileri yüklenemedi. Lütfen tekrar deneyin.')
      } finally {
        loading.value = false
      }
    }

    const addNewCard = () => {
      const newCard = {
        id: null,
        card_type: 'other',
        title_tr: '',
        title_en: '',
        description_tr: '',
        description_en: '',
        background_image: '',
        icon_url: '',
        icon_svg: '',
        is_active: 1,
        sort_order: cards.value.length + 1,
        expanded: true
      }
      cards.value.push(newCard)
    }

    const toggleCard = (index) => {
      cards.value[index].expanded = !cards.value[index].expanded
    }

    const deleteCard = async (card, index) => {
      if (!confirm('Bu kartı silmek istediğinizden emin misiniz?')) {
        return
      }

      try {
        if (card.id) {
          // Veritabanından sil
          const response = await adminApi.deleteAboutCard(card.id)
          if (!response || !response.success) {
            alert('Kart silinemedi. Lütfen tekrar deneyin.')
            return
          }
        }
        
        // Listeden kaldır
        cards.value.splice(index, 1)
        alert('Kart başarıyla silindi!')
      } catch (error) {
        console.error('Kart silinemedi:', error)
        alert('Kart silinemedi. Lütfen tekrar deneyin.')
      }
    }

    const saveAllCards = async () => {
      try {
        submitting.value = true

        // Boş başlıklı kartları kontrol et
        const emptyCards = cards.value.filter(card => !card.title_tr.trim())
        if (emptyCards.length > 0) {
          alert('Lütfen tüm kartlar için Türkçe başlık girin.')
          return
        }

        const cardsData = {
          cards: cards.value.map(card => ({
            id: card.id,
            card_type: card.card_type,
            title_tr: card.title_tr,
            title_en: card.title_en,
            description_tr: card.description_tr,
            description_en: card.description_en,
            background_image: card.background_image,
            icon_url: card.icon_url,
            icon_svg: card.icon_svg,
            is_active: card.is_active,
            sort_order: card.sort_order
          }))
        }

        const response = await adminApi.updateAbout(cardsData)
        
        if (response && response.success) {
          alert('Tüm kartlar başarıyla güncellendi!')
          // Güncellenmiş veriyi yeniden yükle
          await loadCards()
        } else {
          alert('Güncelleme sırasında bir hata oluştu.')
        }
      } catch (error) {
        console.error('Kartlar güncellenemedi:', error)
        alert('Kartlar güncellenemedi. Lütfen tekrar deneyin.')
      } finally {
        submitting.value = false
      }
    }

    const getCardTypeLabel = (type) => {
      const labels = {
        mission: 'Misyon',
        vision: 'Vizyon', 
        values: 'Değerler',
        about: 'Hakkımızda',
        team: 'Ekip',
        history: 'Tarihçe',
        other: 'Diğer'
      }
      return labels[type] || 'Diğer'
    }

    const handleImageError = (event) => {
      event.target.style.display = 'none'
    }

    // Lifecycle
    onMounted(() => {
      loadCards()
    })

    return {
      // Data
      loading,
      submitting,
      cards,
      
      // Methods
      loadCards,
      addNewCard,
      toggleCard,
      deleteCard,
      saveAllCards,
      getCardTypeLabel,
      handleImageError
    }
  }
}
</script>

<style scoped>
/* Container */
.admin-about {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

/* Header */
.admin-header {
  margin-bottom: 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.admin-title {
  font-size: 32px;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 15px;
}

.admin-title i {
  color: #e74c3c;
}

/* Loading */
.loading-state {
  text-align: center;
  padding: 60px 20px;
  color: #7f8c8d;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* No Cards */
.no-cards {
  text-align: center;
  padding: 60px 20px;
  color: #7f8c8d;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
}

.no-cards i {
  font-size: 48px;
  color: #bdc3c7;
  margin-bottom: 20px;
}

/* Cards Container */
.cards-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Card Item */
.card-item {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
  overflow: hidden;
}

.card-header {
  padding: 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h3 {
  margin: 0;
  font-size: 18px;
  color: #2c3e50;
  display: flex;
  align-items: center;
  gap: 10px;
}

.card-type-badge {
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 500;
  text-transform: uppercase;
}

.card-type-badge.mission { background: #e3f2fd; color: #1976d2; }
.card-type-badge.vision { background: #f3e5f5; color: #7b1fa2; }
.card-type-badge.values { background: #e8f5e8; color: #388e3c; }
.card-type-badge.about { background: #fff3e0; color: #f57c00; }
.card-type-badge.team { background: #fce4ec; color: #c2185b; }
.card-type-badge.history { background: #f1f8e9; color: #689f38; }
.card-type-badge.other { background: #f5f5f5; color: #616161; }

.card-actions {
  display: flex;
  gap: 10px;
}

/* Card Form */
.card-form {
  padding: 30px;
}

.form-section {
  margin-bottom: 20px;
}

.status-toggle {
  margin-bottom: 20px;
}

.status-toggle label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  color: #2c3e50;
  cursor: pointer;
}

.status-toggle input[type="checkbox"] {
  transform: scale(1.2);
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  margin-bottom: 8px;
  font-weight: 500;
  color: #2c3e50;
}

.form-group input,
.form-group textarea,
.form-group select {
  padding: 12px;
  border: 2px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s ease;
  font-family: inherit;
  resize: vertical;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: #e74c3c;
}

.form-help {
  margin-top: 5px;
  font-size: 12px;
  color: #7f8c8d;
}

/* Image Preview */
.image-preview {
  margin-top: 15px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.image-preview img {
  width: 100%;
  max-height: 300px;
  object-fit: cover;
  display: block;
}

/* Form Actions */
.form-actions {
  margin-top: 30px;
  display: flex;
  justify-content: center;
}

/* Buttons */
.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-add {
  background: #27ae60;
  color: white;
}

.btn-add:hover {
  background: #219a52;
  transform: translateY(-1px);
}

.btn-toggle {
  background: #3498db;
  color: white;
  padding: 8px 12px;
}

.btn-toggle:hover {
  background: #2980b9;
}

.btn-delete {
  background: #e74c3c;
  color: white;
  padding: 8px 12px;
}

.btn-delete:hover:not(:disabled) {
  background: #c0392b;
}

.btn-submit {
  background: #27ae60;
  color: white;
  padding: 15px 30px;
  font-size: 16px;
}

.btn-submit:hover:not(:disabled) {
  background: #219a52;
  transform: translateY(-1px);
}

.btn:disabled {
  background: #95a5a6;
  cursor: not-allowed;
  transform: none;
}

/* Responsive */
@media (max-width: 768px) {
  .admin-about {
    padding: 15px;
  }

  .admin-header {
    flex-direction: column;
    gap: 20px;
    text-align: center;
  }

  .admin-title {
    font-size: 24px;
  }

  .card-form {
    padding: 20px;
  }

  .form-row {
    grid-template-columns: 1fr;
    gap: 15px;
  }

  .card-header {
    flex-direction: column;
    gap: 15px;
    text-align: center;
  }
}
</style>